import { AccountService } from "@/services/account/AccountService"
import { DB } from "@/services/firebase/db"
import { Auth } from "@/services/firebase/auth"
import Collaborator, { CollaborationState } from "@/types/collaborator/Collaborator"

export class CollaboratorService {
  private accountService = new AccountService()

  public addCollaboratorInvitation (ownerProjectId: string, collaboratorEmail: string, collaboratorSalary: number) {
    return this.accountService.getUserId(collaboratorEmail)
      .then((collaboratorUserId: string | null) => {
        if (!collaboratorUserId || !Auth.currentUser) throw new Error('timetrack.collab.error.userNotFound')
        if (collaboratorUserId === Auth.currentUser.uid) throw new Error('timetrack.collab.error.selfInvite')
        return this.getCollaboratorFromProject(ownerProjectId, collaboratorEmail)
          .then((collaboratorId: string | null) => {
            if (collaboratorId) throw new Error('timetrack.collab.error.userAlreadyInvited')
            return DB.collection('collaborators').doc().set({
              ownerId: Auth.currentUser?.uid,
              projectId: ownerProjectId,
              collaboratorUserId: collaboratorUserId,
              collaboratorMail: collaboratorEmail.toLowerCase(),
              collaboratorSalary: collaboratorSalary,
              state: CollaborationState.invited
            })
          })
      })
  }

  public getCollaboratorsForProject (ownerProjectId: string) {
    return DB.collection('collaborators')
      .where('projectId', '==', ownerProjectId)
      .where('state', '!=', 'delete')
      .get()
      .then((query) => {
        return query.docs.map((doc) => {
          return new Collaborator({
            id: doc.id,
            ...doc.data()
          })
        })
      })
  }

  public getCollaboratorFromProject (ownerProjectId: string, collaboratorMail: string) {
    return DB.collection('collaborators')
      .where('projectId', '==', ownerProjectId)
      .where('collaboratorMail', '==', collaboratorMail)
      .limit(1)
      .get()
      .then((query) => {
        if (query.docs && query.docs.length > 0) {
          return query.docs[0].id
        } else {
          return null
        }
      })
  }

  public getCollaboratorsFromProject (ownerProjectId: string) {
    return DB.collection('collaborators')
      .where('projectId', '==', ownerProjectId)
      .get()
      .then((query) => {
        return query.docs.map((doc) => {
          return new Collaborator({
            id: doc.id,
            ...doc.data()
          })
        })
      })
  }

  public getCollaboratorsForProjects (ownerProjectIds: (string|null)[]) {
    return DB.collection('collaborators')
      .where('projectId', 'in', ownerProjectIds)
      .get()
      .then((query) => {
        return query.docs.map((doc) => {
          return new Collaborator({
            id: doc.id,
            ...doc.data()
          })
        })
      })
  }

  public getCollaboratorFromCollaboratorProject (collaboratorProjectId: string) {
    return DB.collection('collaborators')
      .where('collaboratorProjectId', '==', collaboratorProjectId)
      .limit(1)
      .get()
      .then((query) => {
        if (query.docs && query.docs.length > 0) {
          return query.docs[0].id
        } else {
          return null
        }
      })
  }

  public getProjectForCustomerByState (state: CollaborationState) {
    return DB.collection('collaborators')
      .where('collaboratorMail', '==', Auth.currentUser?.email?.toLowerCase())
      .where('state', '==', state)
      .get()
      .then((query) => {
        return query.docs.map((doc) => {
          return new Collaborator({
            id: doc.id,
            ...doc.data()
          })
        })
      })
  }

  public setCollaboratorState (collaboratorId: string, state: CollaborationState) {
    // TODO state disabled: disable tracking for collabs on this project?
    return DB.collection('collaborators').doc(collaboratorId)
      .set({
        state: state
      }, { merge: true })
  }

  public setCollaborationNewProjectId (collaboratorId: string, newProjectId: string) {
    return DB.collection('collaborators').doc(collaboratorId)
      .set({
        collaboratorProjectId: newProjectId
      }, { merge: true })
  }

  public getCollaborationIdByCollaboratorProjectId (projectId: string) {
    return DB.collection('collaborators')
      .where('collaboratorProjectId', '==', projectId)
      .limit(1)
      .get()
      .then((query) => {
        return new Collaborator({
          id: query.docs[0].id,
          ...query.docs[0].data()
        })
      })
  }

  public setCollaboratorSalary (ownerProjectId: string, collaboratorId: string, salary: number) {
    return DB.collection('collaborators').doc(collaboratorId)
      .set({
        collaboratorSalary: salary
      }, { merge: true })
  }

  public removeCollaboratorFromProject (collaboratorId: string) {
    // TODO how to handle on collaborator side? ask if (keep project and make owner) or remove?
    return DB.collection('collaborators').doc(collaboratorId).delete()
  }
}
